'use client';

import React, { useState, useMemo } from 'react';
import { Plus, Download, Filter, Search, SlidersHorizontal } from 'lucide-react';
import CampaignKPICards from './CampaignKPICards';
import CampaignTable from './CampaignTable';
import CampaignCreateModal from './CampaignCreateModal';

export interface Campaign {
  id: string;
  name: string;
  client: string;
  platform: 'facebook' | 'instagram' | 'both';
  status: 'active' | 'paused' | 'draft' | 'review' | 'completed';
  budget: number;
  spend: number;
  roas: number;
  ctr: number;
  impressions: number;
  reach: number;
  adSets: number;
  startDate: string;
  endDate: string;
  objective: string;
}

export const campaigns: Campaign[] = [
  { id: 'camp-001', name: 'Summer Sale — Hero Banner', client: 'UrbanWear Co.', platform: 'both', status: 'active', budget: 5000, spend: 3820, roas: 1.6, ctr: 0.61, impressions: 412000, reach: 198000, adSets: 3, startDate: 'Jul 15', endDate: 'Aug 15', objective: 'Conversions' },
  { id: 'camp-002', name: 'Q3 Product Launch', client: 'Nexus Tech', platform: 'facebook', status: 'active', budget: 8500, spend: 7650, roas: 2.1, ctr: 1.24, impressions: 680000, reach: 310000, adSets: 5, startDate: 'Jul 28', endDate: 'Aug 28', objective: 'Lead Generation' },
  { id: 'camp-003', name: 'Autumn Harvest Collection', client: 'Verde Organic', platform: 'instagram', status: 'active', budget: 3200, spend: 1840, roas: 5.3, ctr: 2.18, impressions: 290000, reach: 140000, adSets: 2, startDate: 'Aug 1', endDate: 'Aug 31', objective: 'Conversions' },
  { id: 'camp-004', name: 'Morning Ritual — Brand Awareness', client: 'Bravo Coffee', platform: 'both', status: 'active', budget: 4200, spend: 2100, roas: 4.8, ctr: 1.87, impressions: 520000, reach: 260000, adSets: 4, startDate: 'Jul 20', endDate: 'Aug 20', objective: 'Brand Awareness' },
  { id: 'camp-005', name: 'Luxury Living Room Reveal', client: 'Luxe Living', platform: 'instagram', status: 'active', budget: 6000, spend: 3400, roas: 3.7, ctr: 1.52, impressions: 380000, reach: 175000, adSets: 3, startDate: 'Jul 25', endDate: 'Aug 25', objective: 'Traffic' },
  { id: 'camp-006', name: 'FitTrack App Install Drive', client: 'FitTrack Pro', platform: 'facebook', status: 'active', budget: 7200, spend: 4800, roas: 4.1, ctr: 1.93, impressions: 610000, reach: 290000, adSets: 6, startDate: 'Aug 1', endDate: 'Sep 1', objective: 'App Installs' },
  { id: 'camp-007', name: 'Solar Panel ROI Calculator', client: 'Solaris Solar', platform: 'facebook', status: 'paused', budget: 3500, spend: 2800, roas: 2.9, ctr: 0.94, impressions: 245000, reach: 118000, adSets: 2, startDate: 'Jul 10', endDate: 'Aug 10', objective: 'Lead Generation' },
  { id: 'camp-008', name: 'Vet Recommended — Dog Food', client: 'PetPal Supply', platform: 'instagram', status: 'paused', budget: 2800, spend: 2100, roas: 3.4, ctr: 1.41, impressions: 195000, reach: 92000, adSets: 2, startDate: 'Jul 18', endDate: 'Aug 18', objective: 'Conversions' },
  { id: 'camp-009', name: 'Back to School — Flash Sale', client: 'UrbanWear Co.', platform: 'both', status: 'review', budget: 4500, spend: 0, roas: 0, ctr: 0, impressions: 0, reach: 0, adSets: 3, startDate: 'Aug 10', endDate: 'Aug 20', objective: 'Conversions' },
  { id: 'camp-010', name: 'New Blend Launch — Limited Ed.', client: 'Bravo Coffee', platform: 'instagram', status: 'draft', budget: 2200, spend: 0, roas: 0, ctr: 0, impressions: 0, reach: 0, adSets: 1, startDate: 'Aug 15', endDate: 'Sep 15', objective: 'Brand Awareness' },
  { id: 'camp-011', name: 'Smart Home Bundle Promo', client: 'Nexus Tech', platform: 'both', status: 'completed', budget: 5500, spend: 5500, roas: 3.2, ctr: 1.15, impressions: 490000, reach: 225000, adSets: 4, startDate: 'Jun 1', endDate: 'Jul 1', objective: 'Conversions' },
  { id: 'camp-012', name: 'Organic Skincare — Retargeting', client: 'Verde Organic', platform: 'facebook', status: 'active', budget: 1800, spend: 940, roas: 6.1, ctr: 2.74, impressions: 88000, reach: 42000, adSets: 2, startDate: 'Aug 3', endDate: 'Aug 24', objective: 'Conversions' },
];

export default function CampaignManagementContent() {
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const clients = useMemo(() => ['all', ...Array.from(new Set(campaigns.map((c) => c.client)))], []);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.client.toLowerCase().includes(search.toLowerCase());
      const matchClient = filterClient === 'all' || c.client === filterClient;
      const matchStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchPlatform = filterPlatform === 'all' || c.platform === filterPlatform || (filterPlatform !== 'both' && c.platform === 'both');
      return matchSearch && matchClient && matchStatus && matchPlatform;
    });
  }, [search, filterClient, filterStatus, filterPlatform]);

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Ad Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">{campaigns.length} campaigns across {clients.length - 1} clients</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs">
            <Download size={14} />
            Export
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary text-sm"
          >
            <Plus size={15} />
            New Campaign
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <CampaignKPICards campaigns={campaigns} />

      {/* Filters */}
      <div className="card-base p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search campaigns or clients…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-9"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <SlidersHorizontal size={13} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">Filter:</span>
            </div>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="input-base text-xs py-1.5 w-auto pr-8 min-w-[140px]"
            >
              <option value="all">All Clients</option>
              {clients.slice(1).map((c) => (
                <option key={`client-opt-${c}`} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input-base text-xs py-1.5 w-auto pr-8 min-w-[120px]"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="review">In Review</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
            </select>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="input-base text-xs py-1.5 w-auto pr-8 min-w-[130px]"
            >
              <option value="all">All Platforms</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="both">Both</option>
            </select>
            {(search || filterClient !== 'all' || filterStatus !== 'all' || filterPlatform !== 'all') && (
              <button
                onClick={() => { setSearch(''); setFilterClient('all'); setFilterStatus('all'); setFilterPlatform('all'); }}
                className="text-xs text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">{filtered.length} of {campaigns.length} campaigns</p>
      </div>

      {/* Table */}
      <CampaignTable campaigns={filtered} />

      {/* Create Modal */}
      <CampaignCreateModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </div>
  );
}